import { useMemo } from 'react';
import { Location } from 'react-router-dom';
import { getAllWorkflows, getWorkflowByKey } from '../utils/workflowRegistry';

// ============= Types =============
export interface WorkflowItem {
  name: string;
  icon: string;
  description: string;
  path: string;
}

export interface PageState {
  isRequestDetailsPage: boolean;
  isDashboardPage: boolean;
  isWorkflowPage: boolean;
  isActivitiesPage: boolean;
  isFilesPage: boolean;
  requestId: string | null;
  activityId: string | null;
  fileId: string | null;
  currentWorkflow: WorkflowItem | null | undefined;
  currentWorkflowConfig: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

// ============= Hook =============
/**
 * Custom hook to compute page state based on current location
 * Extracts all the complex page state logic from BodyLayout
 */
export const usePageState = (location: Location) => {
  const workflows: WorkflowItem[] = useMemo(
    () =>
      getAllWorkflows().map((wf) => ({
        name: wf.name,
        icon: wf.icon,
        description: wf.description,
        path: `/workflows/${wf.id}`,
      })),
    [] // Empty dependency array since getAllWorkflows() is static
  );

  const pageState = useMemo(() => {
    const path = location.pathname;

    const isRequestDetailsPage = path.startsWith('/request/');
    const isDashboardPage = path === '/';
    const isWorkflowPage = path.startsWith('/workflows/');
    const isActivitiesPage = path.startsWith('/activities');
    const isFilesPage = path.startsWith('/files');

    // Extract IDs for detail pages
    let requestId = null;
    let activityId = null;
    let fileId = null;

    if (isRequestDetailsPage) {
      const parts = path.split('/');
      // If the path is /request/{id}
      if (parts.length === 3) requestId = parts[2];
      // If the path is /request/{workflowType}/{id}
      else if (parts.length > 3) requestId = parts[3];
    }
    if (isActivitiesPage) {
      const parts = path.split('/');
      if (parts.length > 3) {
        activityId = parts[3];
      }
    }
    if (isFilesPage) {
      const parts = path.split('/');
      if (parts.length > 3) {
        fileId = parts[3];
      }
    }

    // Extract workflow as before
    let currentWorkflow = null;
    if (isWorkflowPage) {
      const workflowPath = path.split('/')[2];
      currentWorkflow = workflows.find((w: WorkflowItem) => {
        const wfPath = w.path?.replace('/workflows/', '');
        return wfPath === workflowPath;
      });
    }

    // Get the full workflow configuration for both workflow pages and request details pages
    let currentWorkflowConfig = null;

    if (isWorkflowPage && currentWorkflow) {
      // For workflow pages, extract the workflow key from the path
      const workflowKey = path.split('/')[2];
      currentWorkflowConfig = getWorkflowByKey(workflowKey);
    } else if (isRequestDetailsPage) {
      const parts = path.split('/');
      let workflowKey = null;

      // If the path is /request/{workflowType}/{id}
      if (parts.length > 3) {
        workflowKey = parts[2];
      } else if (parts.length === 3) {
        // For /request/{id}, we need to determine the workflow type from the request data
        // This would require additional context, but for now we'll use a default
        workflowKey = 'dubai-payment-approval'; // Default fallback
      }

      if (workflowKey) {
        currentWorkflowConfig = getWorkflowByKey(workflowKey);
      }
    }

    return {
      isRequestDetailsPage,
      isDashboardPage,
      isWorkflowPage,
      isActivitiesPage,
      isFilesPage,
      requestId,
      activityId,
      fileId,
      currentWorkflow,
      currentWorkflowConfig,
    };
  }, [location.pathname, workflows]);

  return { pageState, workflows };
};
